import * as React from "react";
import { ChatMessageBubble } from "@/components/chat/ChatMessageBubble";
import { useStreamingText } from "@/hooks/useStreamingText";
import type { StreamMessage, Artefact, QaProgressStep } from "../chat-ui.types";
import type { Evidence } from "@/types/evidences";

const PROGRESS_LABELS: Record<QaProgressStep, string> = {
  understanding: "Understanding your question",
  reviewing_history: "Reviewing the conversation",
  rewriting: "Refining your question",
  planning: "Planning the search",
  selecting_documents: "Identifying relevant documents",
  searching: "Searching for relevant passages",
  generating: "Writing the answer",
};

interface Props {
  message: StreamMessage;
  evidences: Evidence[];
  isStreaming: boolean;
  onArtefactClick?: (artefactId: string) => void;
  inlineArtefacts?: Artefact[];
}

export const AssistantMessage = React.memo(
  ({ message, evidences, isStreaming, onArtefactClick, inlineArtefacts }: Props) => {
    const animatedContent = useStreamingText(message.content, isStreaming);
    const animatedMessage = React.useMemo(() => ({ ...message, content: animatedContent }), [message, animatedContent]);
    // Only until the first token: from then on the answer itself shows progress.
    const progressLabel =
      isStreaming && !message.content && message.progressStep ? PROGRESS_LABELS[message.progressStep] : undefined;

    return (
      <>
        {message.answerSource === "history" && (
          <p className="mb-1 pl-3 text-xs text-muted-foreground">
            The following response was generated from the conversation history
          </p>
        )}
        <ChatMessageBubble
          message={animatedMessage}
          evidences={evidences}
          isStreaming={isStreaming}
          source="qa"
          onArtefactClick={onArtefactClick}
          inlineArtefacts={inlineArtefacts}
          actions={{
            showCopy: true,
          }}
          loaderClassName="text-coral"
          loaderLabel={progressLabel}
          loaderAlign="start"
        />
      </>
    );
  },
);
